// Import react lib
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

// Import services
import {
    fetchProducts,
    fetchProduct,
    createProduct as serviceCreateProduct,
    deleteProduct as serviceDeleteProduct,
    editProduct as serviceEditProduct,
    addImageToProduct as serviceAddImageToProduct,
    deleteImage as serviceDeleteImage,
} from '../services/products';

// Include libs
import { areImageListsEquals } from '../../utils/libs/imageHelper';

// ------------------------------------------------------------------------------
// FETCHER
// ------------------------------------------------------------------------------

// Use effect to get all products from the query client
export function useFetchProducts() {
    return useQuery({
        queryKey: ['products'],
        queryFn: fetchProducts,
    });
}

// Use effect to get a specific product from the product list
export function useFetchProduct(productId) {
    const [product, setProduct] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    const {
        data: products,
        isLoading: isQueryLoading,
        isFetching: isQueryFetching,
        isError: isQueryError,
        refetch: refetchProducts,
    } = useQuery({
        queryKey: ['products'],
        queryFn: fetchProducts,
    });

    useEffect(() => {
        // Reset is loading and is error states
        setProduct(null);
        setIsLoading(true);
        setIsError(false);

        // If the product list is empty
        if (!products || !products.length > 0) {
            setIsLoading(false);
            return;
        }

        // If the id is empty return
        if (!productId) {
            setIsLoading(false);
            return;
        }

        // If the query is loading
        if (isQueryLoading || isQueryFetching) {
            setIsLoading(true);
            return;
        }

        // If there is an error fetching products
        if (isQueryError) {
            setIsLoading(false);
            setIsError(true);
            return;
        }

        // find the product
        const foundProduct = products.find(
            (product) => product.id === productId,
        );

        setProduct(foundProduct ?? null);
        setIsLoading(false);
        setIsError(false);
    }, [productId, products, isQueryLoading, isQueryFetching, isQueryError]);

    return {
        product,
        isLoading,
        isError,
        refetchProducts,
    };
}

// ------------------------------------------------------------------------------
// UPDATERS
// ------------------------------------------------------------------------------

// Hook to give the component a create product function
export function useCreateProduct() {
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    // Create function
    async function createProduct(
        productName,
        productDescription,
        productIsOffer,
        productPrice,
        productImages,
    ) {
        setIsLoading(true);
        setIsError(false);

        try {
            // Try to create product with its images
            const newProductId = await serviceCreateProduct(
                productName,
                productDescription,
                productIsOffer,
                productPrice,
                productImages,
            );

            return newProductId;
        } catch (err) {
            setIsError(true);
            return false;
        } finally {
            setIsLoading(false);
        }
    }

    // Reset the error state
    function resetError() {
        setIsError(false);
    }

    return { createProduct, isLoading, isError, resetError };
}

// Hook to give the component a delete product function
export function useDeleteProduct() {
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    // Create a function to delete a product and its images
    async function deleteProduct(productId, productImages) {
        setIsLoading(true);
        setIsError(false);

        try {
            // Try to delete a product and its images
            await serviceDeleteProduct(productId, productImages);
            return true;
        } catch (err) {
            setIsError(true);
            return false;
        } finally {
            setIsLoading(false);
        }
    }

    // Reset the error state
    function resetError() {
        setIsError(false);
    }

    return { deleteProduct, isLoading, isError, resetError };
}

// Hook to give the component an edit product function
export function useEditProduct() {
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    // Create function to edit a product
    async function editProduct(
        productId,
        productNewName,
        productNewDescription,
        productNewIsOffer,
        productNewPrice,
    ) {
        setIsLoading(true);
        setIsError(false);

        try {
            // Try to edit the product infos
            await serviceEditProduct(
                productId,
                productNewName,
                productNewDescription,
                productNewIsOffer,
                productNewPrice,
            );
            return true;
        } catch (err) {
            setIsError(true);
            return false;
        } finally {
            setIsLoading(false);
        }
    }

    // Reset the error state
    function resetError() {
        setIsError(false);
    }

    return { editProduct, isLoading, isError, resetError };
}

// Hook to give the component an add image function
export function useAddImageToProduct() {
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    // Create a function to add an image to the product
    async function addImageToProduct(productId, imagePath, imageFile) {
        setIsLoading(true);
        setIsError(false);

        try {
            // Try to add the image
            await serviceAddImageToProduct(productId, imagePath, imageFile);
            return true;
        } catch (err) {
            setIsError(true);
            return false;
        } finally {
            setIsLoading(false);
        }
    }

    // Reset the error state
    function resetError() {
        setIsError(false);
    }

    return { addImageToProduct, isLoading, isError, resetError };
}

// Hook to give the component a delete image function
export function useDeleteImage() {
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    // Create a function to delete an image
    async function deleteImage(imageId, imagePath) {
        setIsLoading(true);
        setIsError(false);

        try {
            // Try to delete the image
            await serviceDeleteImage(imageId, imagePath);
            return true;
        } catch (err) {
            setIsError(true);
            return false;
        } finally {
            setIsLoading(false);
        }
    }

    // Reset the error state
    function resetError() {
        setIsError(false);
    }

    return { deleteImage, isLoading, isError, resetError };
}

// ------------------------------------------------------------------------------
// HELPERS
// ------------------------------------------------------------------------------
// Hook to give a component a function to test if a product have been modified
export function useHaveProductBeenModified(product) {
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    const [haveProductBeenModified, setHaveProductBeenModified] =
        useState(false);

    async function checkProductModification() {
        setIsLoading(true);
        setIsError(false);
        setHaveProductBeenModified(false);

        if (!product) {
            setIsLoading(false);
            return false;
        }

        // Check if the product have been modified before editing
        try {
            // Get the actual state of the product
            const fetchedProduct = await fetchProduct(product.id);

            if (!fetchProduct) {
                return false;
            }

            // Check is one fo the field is different that the one in the saved query
            const isNameModified = product.name !== fetchedProduct.name;
            const isDescriptionModified =
                product.description !== fetchedProduct.description;
            const isOfferModified = product.isOffer !== fetchedProduct.isOffer;
            const isPriceModified = product.price !== fetchedProduct.price;
            const isImagesModified = !areImageListsEquals(
                product.images,
                fetchedProduct.images,
            );

            // If the product have been modified, return
            if (
                isNameModified ||
                isDescriptionModified ||
                isOfferModified ||
                isPriceModified ||
                isImagesModified
            ) {
                setHaveProductBeenModified(true);
                return false;
            }

            return true;
        } catch (err) {
            setIsError(true);
            return false;
        } finally {
            setIsLoading(false);
        }
    }

    // Reset the error state
    function resetErrors() {
        setIsError(false);
        setHaveProductBeenModified(false);
    }

    return {
        checkProductModification,
        isLoading,
        isError,
        haveProductBeenModified,
        resetErrors,
    };
}
